/**
 * Deleting a section (owner decision #4). Pure, no Firebase.
 *
 * A section that still holds subgroups cannot be deleted: the manager first
 * moves each subgroup to another section of the same organization, or takes
 * it out of any section (parentGroupId cleared), or deletes it. The delete
 * button unlocks once the section is empty.
 */

export interface SectionLike {
  id: string;
  name: string;
  groupType?: string;
  organization?: string | null;
  parentGroupId?: string | null;
  children?: SectionLike[];
}

/** True while `group` is a section that still has subgroups under it. */
export function isSectionDeleteBlocked(group: SectionLike | null | undefined): boolean {
  return !!group && group.groupType === 'parent' && (group.children?.length ?? 0) > 0;
}

/** Every node of the tree, depth first. */
function flatten<T extends SectionLike>(nodes: ReadonlyArray<T>): T[] {
  const out: T[] = [];
  const walk = (list: ReadonlyArray<SectionLike>) => {
    for (const n of list) {
      out.push(n as T);
      walk(n.children || []);
    }
  };
  walk(nodes);
  return out;
}

/**
 * Sections a subgroup of `sectionId` can move to: the organization's other
 * sections, never the one being deleted nor the subgroup itself, by name.
 */
export function sectionMoveTargets<T extends SectionLike>(
  tree: ReadonlyArray<T>,
  sectionId: string,
  subgroupId: string,
): T[] {
  const all = flatten(tree);
  const section = all.find((g) => g.id === sectionId);
  const orgId = section?.organization ?? null;
  return all
    .filter((g) => g.groupType === 'parent' && g.id !== sectionId && g.id !== subgroupId)
    .filter((g) => (g.organization ?? null) === orgId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The tree after `groupId` moved under `targetSectionId` (null: no section,
 * top level). Levels are recomputed; siblings stay sorted by name like
 * getGroupHierarchy.
 */
export function moveGroupNode<T extends SectionLike & { level?: number; children: T[] }>(
  nodes: ReadonlyArray<T>,
  groupId: string,
  targetSectionId: string | null,
): T[] {
  let moved: T | null = null;
  const strip = (list: ReadonlyArray<T>): T[] =>
    list
      .filter((n) => {
        if (n.id !== groupId) return true;
        moved = n;
        return false;
      })
      .map((n) => ({ ...n, children: strip(n.children || []) }));
  const without = strip(nodes);
  if (!moved) return [...nodes];
  const node = { ...(moved as T), parentGroupId: targetSectionId } as T;

  const byName = (a: T, b: T) => a.name.localeCompare(b.name);
  const relevel = (n: T, level: number): T => ({
    ...n,
    level,
    children: ((n.children || []) as T[]).map((c) => relevel(c, level + 1)),
  });

  if (!targetSectionId) {
    return [...without, node].sort(byName).map((n) => relevel(n, 0));
  }
  let placed = false;
  const attach = (list: T[]): T[] =>
    list.map((n) => {
      if (n.id === targetSectionId) {
        placed = true;
        return { ...n, children: [...(n.children || []), node].sort(byName) };
      }
      return { ...n, children: attach(n.children || []) };
    });
  const result = attach(without);
  return (placed ? result : [...result, node].sort(byName)).map((n) => relevel(n, 0));
}
